import {
  ClipboardEvent as ReactClipboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import { Colors, Sizes } from '@/components/ui/types';
import { renderValue, onChangeContentEditable } from './dom';
import { TokenInputProps } from './types';
import { placeCaretAfter } from './utils/caret';
import { serializeRoot } from './utils/serialize';

export { countTokens } from './utils/tokens';
export type {
  TokenConfig,
  TokenDefinition,
  TokenDescriptor,
  TokenInputProps,
  TokenInstance,
  TokenRenderContext,
} from './types';

const sizeClasses: Record<Sizes, string> = {
  xs: 'text-xs min-h-16 px-2 py-1 rounded-sm',
  sm: 'text-sm min-h-20 px-2.5 py-1.5 rounded-md',
  md: 'text-base min-h-24 px-3 py-2 rounded-md',
  lg: 'text-lg min-h-28 px-3.5 py-2.5 rounded-lg',
  xl: 'text-xl min-h-32 px-4 py-3 rounded-lg',
};

const colorBorderClasses: Record<Colors, string> = {
  default:
    'border-base-content/40 focus:border-neutral/40 focus:border-neutral focus:ring-neutral',
  neutral: 'border-neutral/60 focus:border-neutral focus:ring-neutral/40',
  primary: 'border-primary/60 focus:border-primary focus:ring-primary/40',
  secondary:
    'border-secondary/60 focus:border-secondary focus:ring-secondary/40',
  accent: 'border-accent/60 focus:border-accent focus:ring-accent/40',
  info: 'border-info/60 focus:border-info focus:ring-info/40',
  success: 'border-success/60 focus:border-success focus:ring-success/40',
  warning: 'border-warning/60 focus:border-warning focus:ring-warning/40',
  error: 'border-error/60 focus:border-error focus:ring-error/40',
};

const baseClasses = twJoin(
  'w-full border bg-base-100 text-base-content',
  'focus:outline-none focus:ring-2 focus:ring-offset-2',
  'empty:before:content-[attr(data-placeholder)]',
  'empty:before:text-base-content/40',
  'whitespace-pre-wrap break-words',
);

const errorClasses = 'border-error focus:border-error focus:ring-error/40';
const disabledClasses = 'cursor-not-allowed opacity-60';

const TokenInput = forwardRef<HTMLDivElement, TokenInputProps>(
  (
    {
      value,
      onChange,
      onTokensChange,
      tokenConfig,
      placeholder,
      size = 'md',
      color = 'default',
      error = false,
      disabled = false,
      className,
      ...rest
    },
    forwardedRef,
  ) => {
    const editorRef = useRef<HTMLDivElement | null>(null);
    const lastEmittedValueRef = useRef(value);

    useEffect(() => {
      const root = editorRef.current;
      if (!root) return;
      if (serializeRoot(root, tokenConfig) === value) return;
      const tokens = renderValue(root, value, tokenConfig);
      lastEmittedValueRef.current = value;
      onTokensChange?.(tokens);
    }, [value, tokenConfig, onTokensChange]);

    const handleInput = useCallback(() => {
      const root = editorRef.current;
      if (!root) return;
      const { lastInserted, tokens } = onChangeContentEditable(
        root,
        tokenConfig,
      );
      if (lastInserted) placeCaretAfter(lastInserted);
      const nextValue = serializeRoot(root, tokenConfig);
      if (nextValue !== lastEmittedValueRef.current) {
        lastEmittedValueRef.current = nextValue;
        onChange(nextValue);
      }
      onTokensChange?.(tokens);
    }, [tokenConfig, onChange, onTokensChange]);

    const serializeSelection = useCallback((): string => {
      const root = editorRef.current;
      if (!root) return '';
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed)
        return serializeRoot(root, tokenConfig);

      const range = selection.getRangeAt(0);
      if (!root.contains(range.commonAncestorContainer)) {
        return serializeRoot(root, tokenConfig);
      }
      const wrapper = document.createElement('div');
      wrapper.appendChild(range.cloneContents());
      return serializeRoot(wrapper, tokenConfig);
    }, [tokenConfig]);

    const handleCopy = useCallback(
      (event: ReactClipboardEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.clipboardData.setData('text/plain', serializeSelection());
      },
      [serializeSelection],
    );

    const handleCut = useCallback(
      (event: ReactClipboardEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.clipboardData.setData('text/plain', serializeSelection());
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
          selection.getRangeAt(0).deleteContents();
          handleInput();
        }
      },
      [serializeSelection, handleInput],
    );

    const classes = twMerge(
      baseClasses,
      sizeClasses[size],
      colorBorderClasses[color],
      error && errorClasses,
      disabled && disabledClasses,
      className,
    );

    useImperativeHandle(
      forwardedRef,
      () => editorRef.current as HTMLDivElement,
    );

    return (
      <div
        {...rest}
        ref={editorRef}
        role="textbox"
        aria-multiline="true"
        aria-placeholder={placeholder}
        aria-disabled={disabled || undefined}
        aria-invalid={error || undefined}
        contentEditable={!disabled}
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onCopy={handleCopy}
        onCut={handleCut}
        className={classes}
        onInput={handleInput}
      />
    );
  },
);

TokenInput.displayName = 'TokenInput';

export default TokenInput;
